import { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import { useLanguage } from '@/i18n/language-context';
import { isPrivateRoute } from '@/donor/routes';

import {
  publicPage,
  trackPublicEvent,
  type AnalyticsProperties,
  type PublicAnalyticsEvent,
} from './analytics';

export const useAnalytics = () => {
  const { locale } = useLanguage();
  const { pathname } = useLocation();
  return useCallback(
    (event: PublicAnalyticsEvent, properties: AnalyticsProperties = {}) => {
      if (isPrivateRoute(pathname)) return;
      trackPublicEvent(event, { page: publicPage(pathname), language: locale, ...properties });
    },
    [pathname, locale],
  );
};

/** Track a loaded resource or terminal error once per navigation, across refetches and StrictMode. */
export const useAnalyticsView = (
  event: PublicAnalyticsEvent,
  identity: string | null,
  properties: AnalyticsProperties,
): void => {
  const track = useAnalytics();
  const location = useLocation();
  const lastView = useRef<string | null>(null);
  useEffect(() => {
    if (identity === null) return;
    const view = `${location.key}:${event}:${identity}`;
    if (lastView.current === view) return;
    lastView.current = view;
    track(event, properties);
  }, [event, identity, location.key, properties, track]);
};
